import { XIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { Button } from '@repo/ui';

import { useLatestNews } from '@features/parkingMap/hooks/useLatestNews';
import { useNewsStore } from '@features/parkingMap/stores/useNewsStore';

interface Props {
  testId: TestId;
}

// Latest website news over the map: a top toast on mobile (title cut to one
// line), the top-right corner on desktop (whole title).
export default function NewsToast({ testId }: Props) {
  const latestNews = useLatestNews();
  const dismissedGuids = useNewsStore((state) => state.dismissedGuids);
  const dismiss = useNewsStore((state) => state.dismiss);

  const handleDismiss = () => {
    if (latestNews === undefined) return;
    dismiss(latestNews.guid);
  };

  if (latestNews === undefined || dismissedGuids.includes(latestNews.guid)) return null;

  return (
    <div
      className="bg-primary border-stroke-base-medium z-floating absolute top-4 right-4 left-4 flex items-center gap-2 rounded-xl border py-1 pr-1 pl-4 shadow-lg md:left-auto md:max-w-100"
      data-testid={testId}
    >
      <span className="text-brand shrink-0 self-start py-1.5 text-sm font-semibold">{tr('map:news.label')}</span>
      <a
        href={latestNews.url}
        target="_blank"
        className="text-header min-w-0 flex-1 truncate py-1.5 text-sm font-light hover:underline md:whitespace-normal"
        data-testid={testId.append('link')}
      >
        {latestNews.title}
      </a>
      <Button
        color="ghost"
        size="sm"
        iconOnly
        leftIcon={XIcon}
        onClick={handleDismiss}
        aria-label={tr('common:actions.close')}
        testId={testId.append('dismissButton')}
      />
    </div>
  );
}
